// hooks/useOnboardingProgress.js
//
// Single hook the merchant app uses to read the backend onboarding progress/gate
// model. All gating decisions in the UI derive from this — the UI never re-derives
// gate logic locally (backend is the source of truth).

import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { fetchOnboardingProgress } from '../services/onboardingProgressService';

const EMPTY = Object.freeze({});

/**
 * @param {object} [options] passthrough react-query options (e.g. { enabled })
 */
export default function useOnboardingProgress(options = EMPTY) {
  const parentClientId = useSelector((s) => s.auth?.parentClientId) || localStorage.getItem('parentClientId') || null;
  const token = useSelector((s) => s.auth?.token) || localStorage.getItem('authToken') || null;
  const isAuthenticated =
    useSelector((s) => s.auth?.isAuthenticated) || !!localStorage.getItem('authToken');

  const enabled = !!parentClientId && !!isAuthenticated && options.enabled !== false;

  const query = useQuery({
    queryKey: ['onboardingProgress', parentClientId],
    queryFn: () => fetchOnboardingProgress(parentClientId, token),
    enabled,
    staleTime: 60_000,
    retry: 1,
    refetchOnWindowFocus: true,
  });

  const progress = query.data || null;

  // Backend contract is snake_case both ways (POCAS/OMS map to camelCase internally). This hook is
  // the single place that reads the wire format and normalises it into the app's internal shape,
  // so components never deal with snake_case directly.
  const g = progress || {};

  return {
    progress,
    phases: g.phases || [],
    status: g.status ?? null,
    completionPercent: g.completion_percent ?? 0,
    nextIncompletePhase: g.next_incomplete_phase ?? null,
    // Definitive gate signals (undefined until loaded).
    businessOpsUnlocked: g.business_ops_unlocked,
    walletRechargeAllowed: g.wallet_recharge_allowed,
    moneyOutUnlocked: g.money_out_unlocked,
    bankVerified: g.bank_verified,
    agreementRequired: g.agreement_required ?? false,
    agreementSigned: g.agreement_signed ?? false,
    // Query lifecycle.
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
    enabled,
  };
}
